import { HttpClient } from "@angular/common/http";
import { DestroyRef, Injectable } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { TQuickActionTag } from "@common/microservice-types/db/quick-action/quick-action.default";
import { IQuickActionUser } from "@common/microservice-types/db/quick-action/quick-action.user";
import { Core } from "@core/core";
import { TranslateService } from "@ngx-translate/core";
import { Observable, map } from "rxjs";

export type IQuickActionCreateDto = Pick<IQuickActionUser, 'title' | 'value' | 'tag'>;

@Injectable({ providedIn: "root" })
export class QuickActionsService
{
    private isInitialized = false;
    private readonly baseUrl = '/api/r2-chat/quick-actions';

    constructor(
        private http: HttpClient,
        private translateService: TranslateService,
        private core: Core,
        private destroyRef: DestroyRef,
    ) { }

    /** Starts localized quick-action loading and keeps it in sync with language changes. */
    public async initLocalizedQuickActions(): Promise<void>
    {
        if (this.isInitialized) { return; }
        this.isInitialized = true;

        await this.syncActionsForLanguage(this.translateService.currentLang);

        this.translateService.onLangChange
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(({ lang }) =>
            {
                this.syncActionsForLanguage(lang);
            });
    }

    public async reload(): Promise<void>
    {
        await this.syncActionsForLanguage(this.translateService.currentLang);
    }

    public getAll(): Observable<IQuickActionUser[]>
    {
        return this.fetchForLocale(this.translateService.currentLang);
    }

    public getByTag(tag: string): Observable<IQuickActionUser[]>
    {
        return this.getAll().pipe(
            map((items) => items.filter((item) => item.tag?.includes(tag as TQuickActionTag) || item.tag?.includes("global"))),
        );
    }

    public create(dto: IQuickActionCreateDto): Observable<IQuickActionUser>
    {
        return this.http.post<IQuickActionUser>(this.baseUrl, dto);
    }

    public update(id: number, dto: Partial<IQuickActionCreateDto>): Observable<IQuickActionUser>
    {
        return this.http.patch<IQuickActionUser>(`${ this.baseUrl }/${ id }`, dto);
    }

    public reorder(ids: number[]): Observable<void>
    {
        const items = ids.map((id, index) => ({ id, order: index + 1 }));
        return this.http.put<void>(`${ this.baseUrl }/reorder`, { items });
    }

    public duplicate(id: number): Observable<IQuickActionUser>
    {
        return this.http.post<IQuickActionUser>(`${ this.baseUrl }/${ id }/duplicate`, {});
    }

    public delete(id: number): Observable<void>
    {
        return this.http.delete<void>(`${ this.baseUrl }/${ id }`);
    }

    public deleteBulk(ids: number[]): Observable<void>
    {
        return this.http.delete<void>(this.baseUrl, { body: { ids } });
    }

    private fetchForLocale(lang: string): Observable<IQuickActionUser[]>
    {
        const locale = (lang || 'en').toLowerCase().split('-')[ 0 ];
        return this.http.get<IQuickActionUser[]>(this.baseUrl, { params: { locale } });
    }

    private async syncActionsForLanguage(lang: string): Promise<void>
    {
        return new Promise((resolve) =>
        {
            this.fetchForLocale(lang).subscribe((actions) =>
            {
                this.core.chatSetting.setQuickActions(actions);
                resolve();
            });
        });
    }
}
